import {
	Button,
	Checkbox,
	FormControl,
	FormControlLabel,
	FormHelperText,
	Grid2,
	LinearProgress,
	TextField,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useEffect, useMemo, useState } from "react";
import { Controller, type SubmitHandler, useForm } from "react-hook-form";
import { ErrorModal } from "../../../../components/modals/error-modal";
import MesfModal from "../../../../components/modals/modal.mesf";
import { ButtonWithLoading } from "../../../../components/shared/buttons/button-with-loading";
import { useUserContext } from "../../../../context/userContext";
import {
	AssetsFilter,
	useAssets,
} from "../../../../controls/asset-filter/AssetFilter";
import { NotificationSnackBar } from "../../../../controls/snackbars/notification-snackbar";
import { HelmetDexteel } from "../../../../helmet/HelmetDexteel";
import { getContextAssetId } from "../OperatorsPage";
import {
	INITIAL_VALUES,
	type OperatorFormValues,
} from "../models/FormInitialValues";
import type { Operator } from "../models/Operator";
import {
	deleteOperator,
	getOperatorAssets,
	upsertOperator,
} from "../repositories/OperatorsRepository";

type Props = {
	modal: "create" | "update" | "delete" | "";
	onHide: (shouldUpdate: boolean) => void;
	operator: Operator | null;
};

export const UpsertDeleteModal = ({ modal, onHide, operator }: Props) => {
	const {
		state: { defaultAreaId },
	} = useUserContext();
	const assetId = getContextAssetId(defaultAreaId);
	const [message, setMessage] = useState("");
	const [error, setError] = useState("");
	const queryClient = useQueryClient();

	const { control, setValue, reset, handleSubmit } =
		useForm<OperatorFormValues>({
			defaultValues: INITIAL_VALUES,
		});

	useAssets((assetsError) => setError(assetsError.message));

	const assignedAssetsQuery = useQuery<
		{ AssetId: number; AssetName: string }[],
		Error
	>({
		queryKey: ["operators", "assigned-assets", operator?.OperatorId ?? null],
		enabled: modal === "update" && operator?.OperatorId != null,
		queryFn: ({ signal }) => getOperatorAssets(operator!.OperatorId!, signal),
	});

	const isAssetSelectionLoading =
		modal === "update" && assignedAssetsQuery.isLoading;

	const title = useMemo(() => {
		if (modal === "update") {
			return "EDIT";
		}

		if (modal === "delete") {
			return "DELETE";
		}

		return "CREATE";
	}, [modal]);

	const dialogTitle = useMemo(() => {
		if (modal === "create") {
			return "New Operator - Operators";
		}

		if (modal === "update") {
			return "Edit Operator - Operators";
		}

		if (modal === "delete") {
			return "Delete Operator - Operators";
		}

		return "Operators";
	}, [modal]);

	const mutation = useMutation({
		mutationFn: async (data: OperatorFormValues) => {
			if (modal === "delete") {
				if (operator?.OperatorId == null) {
					throw new Error("OperatorId is required");
				}

				await deleteOperator(operator.OperatorId);
				return;
			}

			const operatorId = await upsertOperator({
				OperatorId: data.OperatorId,
				OperatorName: data.OperatorName,
				AssetIds: data.AssetIds,
				Enabled: data.Enabled,
				IsActive: data.IsActive,
			});

			if (operatorId == null) {
				throw new Error(
					modal === "create"
						? "Error saving operator"
						: "Error updating operator",
				);
			}
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ["operators"] });
			await queryClient.invalidateQueries({
				queryKey: ["operators", "assigned-assets"],
			});
			setMessage(
				`The operator was ${modal === "update" ? "updated" : modal}d succesfully`,
			);
			onHide(true);
		},
		onError: (mutationError: Error) => {
			setError(mutationError.message);
		},
	});

	const onSubmit: SubmitHandler<OperatorFormValues> = async (data) => {
		await mutation.mutateAsync({
			...data,
			OperatorName: data.OperatorName.trim(),
		});
	};

	useEffect(() => {
		if (modal === "") {
			return;
		}

		if (modal === "create") {
			const defaultAssetIds = assetId == null ? [] : [assetId];
			reset({
				...INITIAL_VALUES,
				AssetIds: defaultAssetIds,
			});
			return;
		}

		reset({
			...INITIAL_VALUES,
			...(operator || INITIAL_VALUES),
			AssetIds: [],
		});

		if (operator) {
			setValue("OperatorId", operator.OperatorId);
			setValue("OperatorName", operator.OperatorName);
			setValue("Enabled", operator.Enabled);
			setValue("IsActive", operator.IsActive);
		}
	}, [assetId, modal, operator, reset, setValue]);

	useEffect(() => {
		if (modal !== "update") {
			return;
		}

		const assignedAssetIds = (assignedAssetsQuery.data ?? []).map(
			(asset) => asset.AssetId,
		);
		setValue("AssetIds", assignedAssetIds);
	}, [assignedAssetsQuery.data, modal, setValue]);

	useEffect(() => {
		if (assignedAssetsQuery.error) {
			setError(assignedAssetsQuery.error.message);
		}
	}, [assignedAssetsQuery.error]);

	return (
		<>
			{modal !== "" ? <HelmetDexteel title={dialogTitle} /> : null}
			<MesfModal
				title={`${title} OPERATOR`}
				open={modal !== ""}
				handleClose={() => onHide(false)}
				maxWidth="sm"
				id="operator-modal"
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content dividers style={{ padding: "10px 30px" }}>
						<Grid2 container spacing={1}>
							{modal !== "delete" ? (
								<Grid2 size={{ xs: 12 }}>
									{isAssetSelectionLoading ? <LinearProgress /> : null}
									<Controller
										name="AssetIds"
										control={control}
										render={({
											field: { onChange, value },
											fieldState: { error: fieldError },
										}) => {
											const shouldShowNoAssetsHelper =
												!isAssetSelectionLoading && value.length === 0;

											return (
												<FormControl error={!!fieldError} fullWidth>
													<AssetsFilter
														label="Assets"
														multiple
														value={value}
														onChange={onChange}
														filterAreaAssets
														disabled={
															mutation.isPending || isAssetSelectionLoading
														}
													/>
													{shouldShowNoAssetsHelper ? (
														<FormHelperText>
															No assets are currently assigned to this operator.
														</FormHelperText>
													) : null}
													{fieldError ? (
														<FormHelperText>
															{fieldError.message}
														</FormHelperText>
													) : null}
												</FormControl>
											);
										}}
									/>
								</Grid2>
							) : null}
							<Grid2 size={{ xs: 12 }}>
								<Controller
									name="OperatorName"
									control={control}
									rules={{
										required: "Name is required",
										validate: (value) =>
											value.trim().length > 0 || "Name is required",
									}}
									render={({
										field: { onChange, value },
										fieldState: { error: fieldError },
									}) => (
										<FormControl error={!!fieldError} fullWidth>
											<TextField
												label="Name"
												variant="outlined"
												fullWidth
												margin="dense"
												autoComplete="off"
												value={value}
												disabled={modal === "delete" || mutation.isPending}
												onChange={onChange}
											/>
											{fieldError ? (
												<FormHelperText>{fieldError.message}</FormHelperText>
											) : null}
										</FormControl>
									)}
								/>
							</Grid2>
							<Grid2 size={{ xs: 12, sm: 12, md: 4 }}>
								<Controller
									name="Enabled"
									control={control}
									render={({ field }) => (
										<FormControlLabel
											control={
												<Checkbox
													checked={field.value}
													onChange={(event) =>
														field.onChange(event.target.checked)
													}
													name="Enabled"
													color="primary"
													disabled={modal === "delete" || mutation.isPending}
												/>
											}
											label="Enabled"
										/>
									)}
								/>
							</Grid2>
						</Grid2>
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
						<Grid2 container spacing={2} justifyContent="flex-end">
							<Grid2 size={{ md: 3, xs: 12 }}>
								<Button
									fullWidth
									variant="contained"
									color="inherit"
									onClick={() => onHide(false)}
								>
									Cancel
								</Button>
							</Grid2>
							<Grid2 size={{ md: 3, xs: 12 }}>
								<ButtonWithLoading
									fullWidth
									isLoading={mutation.isPending}
									disabled={isAssetSelectionLoading}
									variant="contained"
									color={modal === "delete" ? "secondary" : "primary"}
									type="submit"
								>
									{modal === "delete" ? "Delete" : "Save"}
								</ButtonWithLoading>
							</Grid2>
						</Grid2>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<NotificationSnackBar message={message} onHide={() => setMessage("")} />
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error In Operator"
			/>
		</>
	);
};
